import { apiClient } from '@ipfabric/api-client/apiClient';
import {
  getLabelsAssignmentTargets,
  postLabelsAssignments,
} from '@ipfabric/api-client/client/model/labels/labels';
import { parseDisplayLabel } from '../shared/formatDisplayLabel';
import type {
  LabelAssignmentTarget,
  LabelAssignmentTargetSearchResult,
} from './assignedLabelsTypes';

export type UpdateAssignedLabelBody = {
  label: string;
  reflectToRules?: boolean;
};

// Labels are carried through the UI as the joined "name: value" display token;
// the API stores them split, so convert to a name/value pair at the boundary.
const toRelabelData = ({ label, reflectToRules }: UpdateAssignedLabelBody) => {
  const { name, value } = parseDisplayLabel(label);
  return {
    name,
    ...(value !== undefined ? { value } : {}),
    ...(reflectToRules !== undefined ? { reflectToRules } : {}),
  };
};

export const searchAssignmentTargets = async (
  search: string,
  limit = 50,
): Promise<LabelAssignmentTargetSearchResult[]> => {
  const result = await getLabelsAssignmentTargets({
    search,
    limit: String(limit),
  });
  return result as LabelAssignmentTargetSearchResult[];
};

export const assignLabels = async (
  snapshotId: string,
  labels: string[],
  targets: LabelAssignmentTarget[],
  inherit: boolean,
  reflectToRules?: boolean,
): Promise<void> => {
  await postLabelsAssignments({
    snapshotId,
    labels: labels.map((label) => {
      const { name, value } = parseDisplayLabel(label);
      return value !== undefined && value !== '' ? { name, value } : { name };
    }),
    targets,
    inherit,
    ...(reflectToRules !== undefined ? { reflectToRules } : {}),
  });
};

export const deleteAssignedLabel = (
  snapshotId: string,
  labelId: string,
  reflectToRules?: boolean,
) =>
  apiClient({
    method: 'DELETE',
    url: `/labels/snapshots/${snapshotId}/assignments/${labelId}`,
    ...(reflectToRules ? { params: { reflectToRules: true } } : {}),
  });

export const updateAssignedLabel = (
  snapshotId: string,
  labelId: string,
  body: UpdateAssignedLabelBody,
) =>
  apiClient({
    data: toRelabelData(body),
    headers: { 'Content-Type': 'application/json' },
    method: 'PUT',
    url: `/labels/snapshots/${snapshotId}/assignments/${labelId}`,
  });
